import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiClient } from '@/lib/api';
import { Assignment } from '@/lib/types';

interface CourseAssignmentsProps {
  courseId: number;
}

type AssignmentGroup = {
  key: string;
  title: string;
  items: Assignment[];
};

function groupAssignments(assignments: Assignment[]): AssignmentGroup[] {
  const sectionOrder: string[] = [];
  const bySection = new Map<string, Assignment[]>();
  const courseWide: Assignment[] = [];

  for (const assignment of assignments) {
    if (assignment.section == null) {
      courseWide.push(assignment);
      continue;
    }
    const key = String(assignment.section);
    if (!bySection.has(key)) {
      sectionOrder.push(key);
      bySection.set(key, []);
    }
    bySection.get(key)!.push(assignment);
  }

  const groups: AssignmentGroup[] = sectionOrder.map((key) => {
    const items = bySection.get(key) || [];
    return {
      key,
      title: items[0]?.section_title || 'Section',
      items,
    };
  });

  if (courseWide.length > 0) {
    groups.push({ key: 'course-wide', title: 'Course-wide', items: courseWide });
  }

  return groups;
}

export default function CourseAssignments({ courseId }: CourseAssignmentsProps) {
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [textDraft, setTextDraft] = useState<Record<number, string>>({});
  const [fileDraft, setFileDraft] = useState<Record<number, File | null>>({});
  const [submittingId, setSubmittingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await apiClient.getAssignments(courseId);
    if (res.success && res.data) {
      const items = (res.data as { results?: Assignment[] }).results ?? res.data;
      setAssignments(Array.isArray(items) ? items : []);
    }
    setLoading(false);
  }, [courseId]);

  useEffect(() => {
    load();
  }, [load]);

  const groups = useMemo(() => groupAssignments(assignments), [assignments]);

  if (loading) {
    return null;
  }

  if (assignments.length === 0) return null;

  return (
    <div className="mx-4 mt-3 overflow-y-auto max-h-64 rounded-xl border border-slate-100 bg-white p-4">
      <h3 className="text-sm font-semibold text-slate-900 mb-3">Assignments</h3>
      <div className="space-y-5">
        {groups.map((group) => (
          <div key={group.key} className="space-y-3">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{group.title}</p>
            {group.items.map((assignment) => {
              const mine = assignment.my_submission;
              return (
                <div key={assignment.id} className="rounded-lg border border-slate-100 p-3">
                  <p className="font-medium text-slate-900 text-sm">{assignment.title}</p>
                  {assignment.description && (
                    <p className="mt-1 text-xs text-slate-500 whitespace-pre-wrap">{assignment.description}</p>
                  )}
                  <p className="mt-1 text-xs text-slate-400">Pass mark: {assignment.pass_mark}%</p>
                  {mine ? (
                    <div className="mt-2 text-xs text-slate-600 space-y-1">
                      <p>Status: {mine.status}</p>
                      {mine.grade != null && <p>Grade: {mine.grade}%</p>}
                      {mine.feedback && <p>Feedback: {mine.feedback}</p>}
                    </div>
                  ) : (
                    <p className="mt-2 text-xs text-slate-400">Not submitted</p>
                  )}
                  <textarea
                    value={textDraft[assignment.id] ?? mine?.text ?? ''}
                    onChange={(e) => setTextDraft((prev) => ({ ...prev, [assignment.id]: e.target.value }))}
                    rows={3}
                    className="mt-2 w-full rounded border border-slate-200 px-2 py-1 text-sm"
                    placeholder="Your response"
                  />
                  <input
                    type="file"
                    className="mt-2 block text-xs"
                    onChange={(e) => setFileDraft((prev) => ({ ...prev, [assignment.id]: e.target.files?.[0] || null }))}
                  />
                  <button
                    type="button"
                    disabled={submittingId === assignment.id}
                    onClick={async () => {
                      setSubmittingId(assignment.id);
                      const res = await apiClient.submitAssignment(assignment.id, {
                        text: textDraft[assignment.id] ?? mine?.text ?? '',
                        file: fileDraft[assignment.id],
                      });
                      setSubmittingId(null);
                      if (res.success) load();
                      else alert(res.error || 'Submit failed');
                    }}
                    className="mt-2 rounded bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                  >
                    {submittingId === assignment.id ? 'Submitting…' : mine ? 'Resubmit' : 'Submit'}
                  </button>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
